import { Button, Form, Input, Modal, notification, Alert } from "antd";
import React from "react";
import {
  _initiate_tier1_funding,
  _get_korapay_dynamic_account,
  _verify_tier1_funding,
  _get_user_wallet,
  _call_virtual_account_webhook,
} from "../../common/axios_services";
import { useNavigate } from "react-router-dom";
import { Copy } from "lucide-react";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import useWalletSocket from "../../hooks/useWalletSocket";
import { showErrorNotification } from "../../common/utils/notification";

export default function FundWalletModalContainer({
  children,
  onBalanceUpdate,
}) {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [account, setAccount] = React.useState(null);
  const [waitingForPayment, setWaitingForPayment] = React.useState(false);
  const timeoutRef = React.useRef(null);
  const pollIntervalRef = React.useRef(null);
  const userData = useSelector((state) => state.userProfile.dataFetched);
  const isDev = process.env.REACT_APP_NODE_ENV === "dev";
  const isTier2OrAbove =
    userData?.tier === "tier_2" ||
    userData?.tier === "2" ||
    userData?.tier === "tier_3" ||
    userData?.tier === "3";

  const rawDedicatedNuban = [
    userData?.nubanAccount?.accountNumber,
    userData?.anchorNuban,
  ].find((num) => num && !num.includes("*") && String(num).trim().length > 0);
  const hasDedicatedAccount = isTier2OrAbove && Boolean(rawDedicatedNuban);

  const clearTimers = React.useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  const handlePaymentReceived = React.useCallback(
    async (data) => {
      console.log("Payment received in FundWalletModal:", data);

      clearTimers();

      setWaitingForPayment(false);
      setLoading(false);
      setOpen(false);
      setAccount(null);

      if (onBalanceUpdate) {
        onBalanceUpdate();
      } else {
        window.location.reload();
      }

      toast.success("Payment received successfully!");
    },
    [onBalanceUpdate, clearTimers]
  );

  const handleWalletUpdated = React.useCallback(
    (data) => {
      console.log("Wallet updated in FundWalletModal:", data);

      clearTimers();

      setWaitingForPayment(false);
      setLoading(false);
      setOpen(false);
      setAccount(null);

      if (onBalanceUpdate) {
        onBalanceUpdate();
      }

      toast.success("Wallet updated successfully!");
    },
    [onBalanceUpdate, clearTimers]
  );

  const handleTransactionFailed = React.useCallback((data) => {
    console.log("Transaction failed in FundWalletModal:", data);

    clearTimers();

    setWaitingForPayment(false);
    setLoading(false);
    setOpen(false);
    setAccount(null);

    toast.error("Transaction verification failed. Please try again.");
  }, [clearTimers]);

  const { isConnected } = useWalletSocket(
    handlePaymentReceived,
    handleWalletUpdated,
    handleTransactionFailed
  );

  const createTransferDVA = async ({ amount }) => {
    setLoading(true);
    try {
      const response = await _get_korapay_dynamic_account({
        amount: parseInt(amount),
      });
      const data = response?.data?.data?.raw?.data || response?.data?.data;
      const bankAccount = {
        amount: data?.amount_expected || data?.amount,
        reference: data?.reference,
        accountName: data?.bank_account?.account_name || data?.accountName,
        accountNumber: data?.bank_account?.account_number || data?.accountNumber,
        bankName: data?.bank_account?.bank_name || data?.bankName,
      };

      setAccount(bankAccount);
    } catch (err) {
      showErrorNotification(
        err?.response?.data?.message || err?.message || "Something went wrong"
      );
    } finally {
      setLoading(false);
    }
  };

  const initiateKoraLink = async ({ amount }) => {
    setLoading(true);
    try {
      const response = await _initiate_tier1_funding({
        amount: parseInt(amount),
        currency: "NGN",
        callbackUrl: window.location.origin + window.location.pathname,
      });
      const providerResponse = response?.data?.data?.providerResponse;
      const checkoutUrl = providerResponse?.data?.checkout_url || providerResponse?.checkout_url;
      if (checkoutUrl) {
        window.open(checkoutUrl, "_self");
      } else {
        throw new Error("Payment link could not be generated");
      }
    } catch (err) {
      showErrorNotification(
        err?.response?.data?.message || err?.message || "Something went wrong"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleFundWallet = async (type) => {
    try {
      const values = await form.validateFields();
      const amount = values.amount;
      if (type === "dva") {
        await createTransferDVA({ amount });
      } else if (type === "link") {
        await initiateKoraLink({ amount });
      }
    } catch (err) {
      console.log("Validation failed:", err);
    }
  };

  const handleModalClose = () => {
    setOpen(false);
    setAccount(null);
    handleCancelWaiting();
  };

  const handleCancelWaiting = () => {
    clearTimers();
    setWaitingForPayment(false);
    setLoading(false);
  };

  const verifyPaymentManual = async (showToast = true) => {
    if (!account?.reference) return;
    setLoading(true);
    try {
      const res = await _verify_tier1_funding({ reference: account.reference });
      if (res?.data?.success || res?.status === 200 || res?.status === 201) {
        if (showToast) {
          toast.success("Payment verified successfully!");
        }
        clearTimers();
        setWaitingForPayment(false);
        setLoading(false);
        setOpen(false);
        setAccount(null);

        if (onBalanceUpdate) {
          onBalanceUpdate();
        } else {
          window.location.reload();
        }
      } else {
        if (showToast) {
          toast.error("Payment not completed yet.");
        }
      }
    } catch (err) {
      if (showToast) {
        showErrorNotification(
          err?.response?.data?.message || err?.message || "Payment not completed yet."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInitiatePaymentWait = async () => {
    setWaitingForPayment(true);

    timeoutRef.current = setTimeout(() => {
      setWaitingForPayment(false);
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      toast.error("Payment verification timed out. Please try again.");
    }, 120000);

    pollIntervalRef.current = setInterval(() => {
      verifyPaymentManual(false);
    }, 5000);

    if (isDev) {
      const res = await _call_virtual_account_webhook({
        event: "charge.success",
        data: {
          reference: account.reference,
          amount: account.amount,
          customer: {
            email: userData?.email,
          },
        },
      });
      if (res?.data?.data?.status === "error") {
        setWaitingForPayment(false);
        setOpen(false);
        toast.error(res?.data?.data?.message);
        clearTimers();
      }
    }

    toast.success("Monitoring for payment", {
      description: isConnected
        ? "We'll notify you instantly when payment is received!"
        : "Please wait while we verify your transaction.",
    });
  };

  React.useEffect(() => {
    return () => {
      clearTimers();
    };
  }, [clearTimers]);

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const reference = params.get("reference");

    if (reference && reference.toUpperCase().startsWith("KORA")) {
      const verifyPayment = async () => {
        setLoading(true);
        try {
          const res = await _verify_tier1_funding({ reference });
          if (res?.data?.success || res?.status === 200 || res?.status === 201) {
            toast.success("Payment verified successfully!");
            const url = new URL(window.location.href);
            url.searchParams.delete("reference");
            window.history.replaceState({}, document.title, url.pathname + url.search);

            if (onBalanceUpdate) {
              onBalanceUpdate();
            } else {
              window.location.reload();
            }
          } else {
            toast.error("Payment verification failed.");
            const url = new URL(window.location.href);
            url.searchParams.delete("reference");
            window.history.replaceState({}, document.title, url.pathname + url.search);
          }
        } catch (err) {
          toast.error(
            err?.response?.data?.message || err?.message || "Payment verification failed"
          );
          const url = new URL(window.location.href);
          url.searchParams.delete("reference");
          window.history.replaceState({}, document.title, url.pathname + url.search);
        } finally {
          setLoading(false);
        }
      };

      verifyPayment();
    }
  }, [onBalanceUpdate]);

  return (
    <>
      <div
        onClick={() => {
          setOpen(!open);
        }}
      >
        {children}
      </div>
      <Modal
        centered={true}
        title="Fund Wallet"
        open={open}
        footer={null}
        className="pb-5 pt-5"
        onCancel={handleModalClose}
        closable={!waitingForPayment}
        maskClosable={!waitingForPayment}
      >
        {!isConnected && (
          <Alert
            message="Real-time connection unavailable"
            description="Transaction updates may be delayed. Please refresh if needed."
            type="warning"
            showIcon
            style={{ marginBottom: "16px" }}
          />
        )}

        <Form form={form}>
          {hasDedicatedAccount ? (
            <div className="text-center pb-8 px-4 max-w-[450px] mx-auto space-y-8">
              <p className="text-[16px]">
                Please fund your wallet by bank transfer to your dedicated NUBAN
                bank account:
              </p>
              <div className="space-y-2">
                <h1 className="text-3xl text-gray-600">
                  {userData?.nubanAccount?.bankName || userData?.anchorBank || "9 Payment Service Bank"}
                </h1>
                <h1 className="text-6xl">
                  <span className="flex justify-center gap-6">
                    {rawDedicatedNuban}
                    <Copy
                      className="text-green-700 my-auto"
                      onClick={() => {
                        navigator.clipboard.writeText(rawDedicatedNuban);
                        toast.success("Account number copied to clipboard.");
                      }}
                      style={{ cursor: "pointer" }}
                    />
                  </span>
                </h1>
                <h1 className="text-3xl text-gray-600">
                  {userData?.nubanAccount?.accountName || userData?.userName || (`${userData?.firstName} ${userData?.lastName}`)}
                </h1>
              </div>
            </div>
          ) : account === null ? (
            <Form.Item
              name="amount"
              rules={[
                { required: true, message: "Please input amount" },
                {
                  validator: (_, value) => {
                    if (value && parseFloat(value) <= 0) {
                      return Promise.reject(new Error("Amount must be greater than 0"));
                    }
                    return Promise.resolve();
                  }
                }
              ]}
            >
              <Input
                name="amount"
                type="number"
                inputMode="numeric"
                placeholder="5000"
                prefix="NGN"
              />
            </Form.Item>
          ) : (
            <div className="text-center pb-8 px-4 max-w-[450px] mx-auto space-y-8">
              <p className="text-[16px]">
                Please fund your wallet by bank transfer with the{" "}
                <strong className="text-red-500">EXACT AMOUNT AS SHOWN</strong>{" "}
                to the following account:
              </p>
              <div className="space-y-2">
                <h1 className="text-6xl">
                  <span style={{ fontWeight: "lighter" }}>₦</span>
                  {account?.amount?.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </h1>
                <h1 className="text-6xl">
                  <span className="flex justify-center gap-6">
                    {account.accountNumber}
                    <Copy
                      className="text-green-700 my-auto"
                      onClick={() => {
                        navigator.clipboard.writeText(account.accountNumber);
                        toast.success("Account number copied to clipboard.");
                      }}
                      style={{ cursor: "pointer" }}
                    />
                  </span>
                </h1>
                <h1 className="text-3xl text-gray-600">
                  {account.bankName}&nbsp;|&nbsp;
                  {account.accountName}
                </h1>
              </div>
              <p className="text-[16px]">
                <strong>Important:</strong> This account number is for a{" "}
                <strong className="text-red-500">ONE TIME USE ONLY</strong> and
                valid for the next 20 minutes. Please complete your transfer
                within this timeframe.
              </p>
            </div>
          )}
          <div className="flex justify-between">
            <Button onClick={handleModalClose}>Close</Button>
            {isTier2OrAbove ? (
              <Button
                type="primary"
                onClick={() => {
                  toast.success("Monitoring for payment. We'll notify you instantly when payment is received!");
                  setOpen(false);
                }}
              >
                I have made Transfer
              </Button>
            ) : account === null ? (
              <Button
                type="primary"
                loading={loading}
                disabled={loading}
                onClick={() => handleFundWallet("dva")}
                className="w-full"
              >
                Bank Transfer
              </Button>
            ) : waitingForPayment ? (
              <div className="flex flex-col gap-2 items-end">
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
                  <span className="text-sm text-gray-600">
                    {isConnected
                      ? "Waiting for payment confirmation..."
                      : "Verifying transaction..."}
                  </span>
                </div>
                <Button
                  type="primary"
                  loading={loading}
                  onClick={() => verifyPaymentManual(true)}
                >
                  Verify Payment
                </Button>
              </div>
            ) : (
              <Button
                type="primary"
                loading={loading}
                disabled={loading}
                onClick={handleInitiatePaymentWait}
              >
                I have made Transfer
              </Button>
            )}
          </div>
        </Form>
      </Modal>
    </>
  );
}
